import {
  PreScreeningQuestion,
  QuestionType,
  RadioGridQuickSelect,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  CARDIOVASCULAR_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  MEASUREMENT_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  HORMONE_ATTRIBUTES,
  MENTAL_HEALTH_ATTRIBUTES,
  MENTAL_HEALTH_MEDICATIONS,
  NEUROLOGICAL_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Cardiovascular Safety Study
 * Evaluating cardiovascular safety of investigational medication vs placebo
 * in participants at increased risk of adverse cardiovascular outcomes
 * 
 * Qualification Pathways:
 * 1. Cardiovascular History: Any CV risk factor (heart attack, revascularization, etc.)
 * 2. Type 2 Diabetes Pathway: Must have Type 2 diabetes PLUS at least 2 additional risk factors
 *    (hypertension, high cholesterol, low HDL, or current smoking)
 */
export const curraxScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "bmi27RiskFactors",
    type: QuestionType.CHECKBOX,
    question: "Have you been diagnosed with any of these conditions by a doctor or medical professional?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      { value: "hypertension", label: "High blood pressure (hypertension)" },
      { value: "type2_diabetes", label: "Type 2 diabetes" },
      { value: "dyslipidemia", label: "High cholesterol (dyslipidemia)" },
      { value: "none", label: "None of the above" },
    ],
    disqualifyMessage: "This study requires a BMI of 30 or greater, or specific weight-related health conditions if your BMI is 27-29.9.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        hypertension: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
        ],
        type2_diabetes: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
        ],
        dyslipidemia: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HIGH_CHOLESTEROL, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HIGH_CHOLESTEROL, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    ...standardHeightQuestion,
    helpText: "We'll calculate your BMI (Body Mass Index) using your height and weight to determine study eligibility.",
    attributeMapping: mappers.height({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT,
    }),
  },

  {
    ...standardWeightQuestion,
    minBMI: (allAnswers) => {
      const riskFactors = allAnswers?.["bmi27RiskFactors"];
      if (Array.isArray(riskFactors) && riskFactors.includes("none")) {
        return 30; // Require BMI >= 30 if no comorbidities
      }
      return 27; // BMI >= 27 OK if has comorbidities
    },
    helpText: "We'll calculate your BMI (Body Mass Index) using your height and weight to determine study eligibility.",
    attributeMapping: mappers.weight({
      attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT,
    }),
  },

  {
    id: "cardiovascular_risk_history",
    type: QuestionType.CHECKBOX,
    question: "Are any of the following statements true for you?",
    helpText: "Select all that apply.",
    required: true,
    allowBack: true,
    options: [
      { value: "heart_attack", label: "I have had a heart attack (myocardial infarction)" },
      { value: "stroke", label: "I have had a stroke (not including mini-strokes or TIAs)" },
      { value: "coronary_revascularization", label: "I have had a coronary revascularization (bypass surgery, stent, angioplasty, or atherectomy)" },
      { value: "angina_with_evidence", label: "I suffer from angina (chest pain associated with heart disease) with evidence of abnormalities on testing (EKG, stress test, or a heart scan)" },
      { value: "low_abi", label: "I have been told I have an ankle-brachial index measurement (ABI) less than 0.9" },
      { value: "none", label: "None of the above applies to me" },
    ],
    attributeMapping: mappers.multiSelect({
      valueMap: {
        heart_attack: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        ],
        stroke: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
        ],
        coronary_revascularization: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: true },
        ],
        peripheral_revascularization: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
        angina_with_evidence: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true }, // Using as proxy for documented coronary disease
        ],
        low_abi: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: true },
        ],
      },
      noneValue: "none",
      noneAttributeMap: {
        none: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CORONARY_ARTERY_REVASCULARIZATION, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_PERIPHERAL_ARTERY_DISEASE, booleanAttributeValue: false },
        ],
      },
    }),
  },

  {
    id: "type2_diabetes_status",
    type: QuestionType.RADIO,
    question: "Do you have Type 2 diabetes?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I have Type 2 diabetes" },
      { 
        value: "no", 
        label: "No, I do not have Type 2 diabetes",
        disqualify: (allAnswers) => {
          const cvHistory = allAnswers?.["cardiovascular_risk_history"] as string[];
          
          // If they have CV history, they qualify regardless
          if (Array.isArray(cvHistory) && cvHistory.some(factor => factor !== "none")) {
            return false;
          }
          
          // If they don't have CV history and no T2D, they don't qualify
          return true;
        }
      },
    ],
    disqualifyMessage: "Since you don't have cardiovascular risk factors, you must have Type 2 diabetes to qualify for this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: true },
      ],
      no: [
        { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES_TYPE2, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "additional_risk_factors",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following additional risk factors?",
    helpText: "Select all that apply. Note: If you don't have cardiovascular disease or strong risk factors, you'll need at least 2 of these additional risk factors to qualify.",
    required: true,
    allowBack: true,
    options: [
      { 
        value: "controlled_hypertension", 
        label: "I have high blood pressure (hypertension)",
        disqualify: (allAnswers, currentAnswers) => {
          const cvHistory = allAnswers?.["cardiovascular_risk_history"] as string[];
          const hasT2D = allAnswers?.["type2_diabetes_status"] === "yes";
          
          // If they have CV history, they qualify regardless
          if (Array.isArray(cvHistory) && cvHistory.some(factor => factor !== "none")) {
            return false;
          }
          
          // If they don't have T2D, they should have been disqualified already
          if (!hasT2D) {
            return false; // Let the T2D question handle this
          }
          
          // Check if they have selected at least 2 factors
          if (Array.isArray(currentAnswers)) {
            const validFactors = currentAnswers.filter(f => f !== "none_additional_factors");
            return validFactors.length < 2;
          }
          
          return true; // Default disqualify if something went wrong
        }
      },
      { 
        value: "dyslipidemia_medication", 
        label: "I have high cholesterol requiring medication",
        disqualify: (allAnswers, currentAnswers) => {
          const cvHistory = allAnswers?.["cardiovascular_risk_history"] as string[];
          const hasT2D = allAnswers?.["type2_diabetes_status"] === "yes";
          
          // If they have CV history, they qualify regardless
          if (Array.isArray(cvHistory) && cvHistory.some(factor => factor !== "none")) {
            return false;
          }
          
          // If they don't have T2D, they should have been disqualified already
          if (!hasT2D) {
            return false; // Let the T2D question handle this
          }
          
          // Check if they have selected at least 2 factors
          if (Array.isArray(currentAnswers)) {
            const validFactors = currentAnswers.filter(f => f !== "none_additional_factors");
            return validFactors.length < 2;
          }
          
          return true; // Default disqualify if something went wrong
        }
      },
      { 
        value: "low_hdl", 
        label: "I have been told I have low HDL cholesterol (below 50 mg/dL for women or below 40 mg/dL for men) in the past 6 months",
        disqualify: (allAnswers, currentAnswers) => {
          const cvHistory = allAnswers?.["cardiovascular_risk_history"] as string[];
          const hasT2D = allAnswers?.["type2_diabetes_status"] === "yes";
          
          // If they have CV history, they qualify regardless
          if (Array.isArray(cvHistory) && cvHistory.some(factor => factor !== "none")) {
            return false;
          }
          
          // If they don't have T2D, they should have been disqualified already
          if (!hasT2D) {
            return false; // Let the T2D question handle this
          }
          
          // Check if they have selected at least 2 factors
          if (Array.isArray(currentAnswers)) {
            const validFactors = currentAnswers.filter(f => f !== "none_additional_factors");
            return validFactors.length < 2;
          }
          
          return true; // Default disqualify if something went wrong
        }
      },
      { 
        value: "current_smoker", 
        label: "I am a current tobacco smoker",
        disqualify: (allAnswers, currentAnswers) => {
          const cvHistory = allAnswers?.["cardiovascular_risk_history"] as string[];
          const hasT2D = allAnswers?.["type2_diabetes_status"] === "yes";
          
          // If they have CV history, they qualify regardless
          if (Array.isArray(cvHistory) && cvHistory.some(factor => factor !== "none")) {
            return false;
          }
          
          // If they don't have T2D, they should have been disqualified already
          if (!hasT2D) {
            return false; // Let the T2D question handle this
          }
          
          // Check if they have selected at least 2 factors
          if (Array.isArray(currentAnswers)) {
            const validFactors = currentAnswers.filter(f => f !== "none_additional_factors");
            return validFactors.length < 2;
          }
          
          return true; // Default disqualify if something went wrong
        }
      },
      { 
        value: "none_additional_factors", 
        label: "None of the above applies to me",
        disqualify: (allAnswers) => {
          const cvHistory = allAnswers?.["cardiovascular_risk_history"] as string[];
          
          // If they have CV history, they qualify regardless
          if (Array.isArray(cvHistory) && cvHistory.some(factor => factor !== "none")) {
            return false;
          }
          
          // If they don't have CV history and no additional factors, they don't qualify
          return true;
        }
      },
    ],
    disqualifyMessage: "Since you don't have cardiovascular disease or strong risk factors, you need at least 2 additional risk factors along with Type 2 diabetes to qualify.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        controlled_hypertension: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: true },
        ],
        dyslipidemia_medication: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HIGH_CHOLESTEROL, booleanAttributeValue: true },
        ],
        low_hdl: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HIGH_CHOLESTEROL, booleanAttributeValue: true },
        ],
        current_smoker: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: true },
        ],
      },
      noneValue: "none_additional_factors",
      noneAttributeMap: {
        none_additional_factors: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HYPERTENSION, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HIGH_CHOLESTEROL, booleanAttributeValue: false },
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.IS_SMOKER, booleanAttributeValue: false },
        ],
      },
    }),
  },


  {
    id: "weight_loss_surgery_devices",
    type: QuestionType.RADIO,
    question: "Have you had weight loss surgery or device implantation?",
    helpText: "This includes procedures like gastric bypass, gastric sleeve, gastric banding, duodenal switch, or implanted devices like gastric balloons or gastric banding.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I have had weight loss surgery or device implantation", disqualify: true },
      { value: "no", label: "No, I have not had weight loss surgery or device implantation" },
    ],
    disqualifyMessage: "This study cannot include participants who have had weight loss surgery or device implantation.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 20918, booleanAttributeValue: true }], // had_bariatricsurgery
      no: [{ attributeDefinitionId: 20918, booleanAttributeValue: false }],
    }),
  },

  {
    id: "weight_loss_medications",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these weight loss medications?",
    required: true,
    allowBack: true,
    quickSelect: new RadioGridQuickSelect(
      "I am not currently taking any of these medications",
      "never",
      "Submit my medications",
      {
        allText: "All medications in this group will be marked as 'not currently taking'",
        remainingText: "Remaining medications in this group will be marked as 'not currently taking'",
      },
      "_never",
    ),
    gridLayout: {
      columnHeaders: ["Currently taking", "Not currently taking"],
      rows: [
        {
          label: "Ozempic, Wegovy, Victoza or Saxenda",
          sublabel: "Semaglutide, Liraglutide",
          values: [
            { value: "glp1_current", disqualify: true },
            { value: "glp1_never" },
          ],
        },
        {
          label: "Mounjaro or Zepbound",
          sublabel: "Tirzepatide",
          values: [
            { value: "dual_incretin_current", disqualify: true },
            { value: "dual_incretin_never" },
          ],
        },
        {
          label: "Adipex or Tenuate",
          sublabel: "Phentermine, Diethylpropion",
          values: [
            { value: "sympathomimetic_current", disqualify: true },
            { value: "sympathomimetic_never" },
          ],
        },
        {
          label: "Qsymia",
          sublabel: "Phentermine/Topiramate",
          values: [
            { value: "combination_current", disqualify: true },
            { value: "combination_never" },
          ],
        },
        {
          label: "Xenical or Alli",
          sublabel: "Orlistat",
          values: [
            { value: "lipase_current", disqualify: true },
            { value: "lipase_never" },
          ],
        },
      ],
    },
    disqualifyMessage: "This study requires participants to be willing to stop weight loss medications for the duration of the study.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        glp1: 21327, // Semaglutide/Liraglutide (Ozempic, Wegovy, Victoza, Saxenda)
        sympathomimetic: 21509, // Phentermine (Adipex, Tenuate)
        combination: 23392, // Phentermine/Topiramate (Qsymia)
        lipase: 21508, // Orlistat (Xenical, Alli)
        dual_incretin: 23239, // Tirzepatide (Mounjaro, Zepbound)
      },
    }),
  },

  {
    id: "bupropion_medications",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these medications for depression or smoking cessation?",
    required: true,
    allowBack: true,
    quickSelect: new RadioGridQuickSelect(
      "I am not currently taking any of these medications",
      "never",
      "Submit my medications",
      {
        allText: "All medications in this group will be marked as 'not currently taking'",
        remainingText: "Remaining medications in this group will be marked as 'not currently taking'",
      },
      "_never",
    ),
    gridLayout: {
      columnHeaders: ["Currently taking", "Not currently taking"],
      rows: [
        {
          label: "Wellbutrin or Aplenzin",
          sublabel: "Bupropion (for depression)",
          values: [
            { value: "wellbutrin_current", disqualify: true },
            { value: "wellbutrin_never" },
          ],
        },
        {
          label: "Zyban",
          sublabel: "Bupropion (for smoking cessation)",
          values: [
            { value: "zyban_current", disqualify: true },
            { value: "zyban_never" },
          ],
        },


      ],
    },
    disqualifyMessage: "Participants cannot take bupropion-containing medications for depression or smoking cessation during this study.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        wellbutrin: MENTAL_HEALTH_MEDICATIONS.BUPROPION,
        zyban: MENTAL_HEALTH_MEDICATIONS.BUPROPION,
      },
    }),
  },

  {
    id: "maoi_medications",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these MAOI medications for depression or Parkinson's disease?",
    required: true,
    allowBack: true,
    quickSelect: new RadioGridQuickSelect(
      "I am not currently taking any of these medications",
      "never",
      "Submit my medications",
      {
        allText: "All medications in this group will be marked as 'not currently taking'",
        remainingText: "Remaining medications in this group will be marked as 'not currently taking'",
      },
      "_never",
    ),
    gridLayout: {
      columnHeaders: ["Currently taking", "Not currently taking"],
      rows: [
        {
          label: "Nardil",
          sublabel: "Phenelzine (for depression)",
          values: [
            { value: "phenelzine_current", disqualify: true },
            { value: "phenelzine_never" },
          ],
        },
        {
          label: "Parnate",
          sublabel: "Tranylcypromine (for depression)",
          values: [
            { value: "tranylcypromine_current", disqualify: true },
            { value: "tranylcypromine_never" },
          ],
        },
        {
          label: "Marplan",
          sublabel: "Isocarboxazid (for depression)",
          values: [
            { value: "isocarboxazid_current", disqualify: true },
            { value: "isocarboxazid_never" },
          ],
        },
        {
          label: "Eldepryl or Emsam",
          sublabel: "Selegiline (for Parkinson's or depression)",
          values: [
            { value: "selegiline_current", disqualify: true },
            { value: "selegiline_never" },
          ],
        },
        {
          label: "Aurorix or Manerix",
          sublabel: "Moclobemide (for depression)",
          values: [
            { value: "moclobemide_current", disqualify: true },
            { value: "moclobemide_never" },
          ],
        },
        {
          label: "Azilect",
          sublabel: "Rasagiline (for Parkinson's)",
          values: [
            { value: "rasagiline_current", disqualify: true },
            { value: "rasagiline_never" },
          ],
        },
      ],
    },
    disqualifyMessage: "Participants cannot take MAOI medications during this study or within 14 days before starting.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        phenelzine: 20470, // Phenelzine (Nardil)
        tranylcypromine: 20442, // Tranylcypromine (Parnate)
        isocarboxazid: 20469, // Isocarboxazid (Marplan)
        selegiline: 20471, // Selegiline (Eldepryl, Emsam)
        moclobemide: 20444, // Moclobemide (Aurorix, Manerix)
        rasagiline: 23454, // Rasagiline (Azilect)
      },
    }),
  },

  {
    id: "opioid_medications",
    type: QuestionType.RADIO_GRID,
    question: "Have you taken any of these opioid medications?",
    required: true,
    allowBack: true,
    quickSelect: new RadioGridQuickSelect(
      "I am not currently taking any of these medications",
      "never",
      "Submit my medications",
      {
        allText: "All medications in this group will be marked as 'not currently taking'",
        remainingText: "Remaining medications in this group will be marked as 'not currently taking'",
      },
      "_never",
    ),
    gridLayout: {
      columnHeaders: ["Currently taking", "Not currently taking"],
      rows: [
        {
          label: "Methadone",
          sublabel: "Methadone (for addiction treatment)",
          values: [
            { value: "methadone_current", disqualify: true },
            { value: "methadone_never" },
          ],
        },
        {
          label: "OxyContin or Percocet",
          sublabel: "Oxycodone (for pain management)",
          values: [
            { value: "oxycodone_current", disqualify: true },
            { value: "oxycodone_never" },
          ],
        },
        {
          label: "Suboxone or Subutex",
          sublabel: "Buprenorphine (for addiction treatment)",
          values: [
            { value: "buprenorphine_current", disqualify: true },
            { value: "buprenorphine_never" },
          ],
        },
        {
          label: "MS Contin or Morphine",
          sublabel: "Morphine (for pain management)",
          values: [
            { value: "morphine_current", disqualify: true },
            { value: "morphine_never" },
          ],
        },
        {
          label: "Fentanyl patches or Duragesic",
          sublabel: "Fentanyl (for severe pain)",
          values: [
            { value: "fentanyl_current", disqualify: true },
            { value: "fentanyl_never" },
          ],
        },
        {
          label: "Vicodin or Norco",
          sublabel: "Hydrocodone (for pain management)",
          values: [
            { value: "hydrocodone_current", disqualify: true },
            { value: "hydrocodone_never" },
          ],
        },
        {
          label: "Codeine or Tylenol #3",
          sublabel: "Codeine (for pain management)",
          values: [
            { value: "codeine_current", disqualify: true },
            { value: "codeine_never" },
          ],
        },
        {
          label: "Tramadol or Ultram",
          sublabel: "Tramadol (for pain management)",
          values: [
            { value: "tramadol_current", disqualify: true },
            { value: "tramadol_never" },
          ],
        },
      ],
    },
    disqualifyMessage: "For safety reasons, participants with current long-term opioid use cannot participate in this study.",
    attributeMapping: mappers.medicationGrid({
      medicationMap: {
        methadone: 22489, // Methadone
        oxycodone: 21473, // Oxycodone (OxyContin, Percocet)
        buprenorphine: 22191, // Buprenorphine (Suboxone, Subutex)
        morphine: 21477, // Morphine (MS Contin)
        fentanyl: 26416, // Fentanyl (patches, Duragesic)
        hydrocodone: 21474, // Hydrocodone (Vicodin, Norco)
        codeine: 21475, // Codeine (Tylenol #3)
        tramadol: 21472, // Tramadol (Ultram)
      },
    }),
  },

  {
    id: "recent_cardiovascular_events",
    type: QuestionType.RADIO,
    question: "Have you had a heart attack or stroke within the last 90 days?",
    required: true,
    allowBack: true,
    options: [
      { value: "no", label: "No, I have not had a heart attack or stroke in the last 90 days" },
      { value: "yes", label: "Yes, I have had a heart attack or stroke within the last 90 days", disqualify: true },
    ],
    disqualifyMessage: "For safety reasons, participants who have had a recent heart attack or stroke (within 90 days) cannot participate.",
    attributeMapping: mappers.value({
      no: [{ attributeDefinitionId: 1115, booleanAttributeValue: false }], // had_heartattack_3months
      yes: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_HEART_ATTACK, booleanAttributeValue: true },
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_STROKE_TIA, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "serious_medical_conditions",
    type: QuestionType.RADIO,
    question: "Do you have any of the following serious medical conditions?",
    required: true,
    allowBack: true,
    options: [
      { value: "end_stage_renal", label: "End-stage kidney disease requiring dialysis or kidney transplant", disqualify: true },
      { value: "severe_liver", label: "Liver failure or cirrhosis", disqualify: true },
      { value: "severe_heart_failure", label: "Severe heart failure", disqualify: true },
      { value: "seizure_disorder", label: "Seizure disorder or epilepsy", disqualify: true },
      { value: "eating_disorder", label: "Anorexia nervosa or bulimia under active treatment  ", disqualify: true },
      { value: "none", label: "None of the above applies to me" },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these serious medical conditions.",
    attributeMapping: mappers.value({
      end_stage_renal: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
      ],
      severe_liver: [
        { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_LIVER_DISEASE, booleanAttributeValue: true },
      ],
      severe_heart_failure: [
        { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
      ],
      seizure_disorder: [
        { attributeDefinitionId: NEUROLOGICAL_ATTRIBUTES.HAS_EPILEPSY, booleanAttributeValue: true },
      ],
      eating_disorder: [
        { attributeDefinitionId: MENTAL_HEALTH_ATTRIBUTES.HAS_EATING_DISORDER, booleanAttributeValue: true },
      ],
      none: [],
    }),
  },

  {
    id: "pregnancy_nursing",
    type: QuestionType.RADIO,
    question: "Are you currently pregnant or breastfeeding?",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I am currently pregnant or breastfeeding", disqualify: true },
      { value: "not_applicable", label: "Not applicable to me" },
      { value: "no", label: "No, I am not pregnant or breastfeeding" },
    ],
    disqualifyMessage: "For safety reasons, pregnant or breastfeeding participants cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: false },
      ],
      not_applicable: [],
      yes: [
        { attributeDefinitionId: HORMONE_ATTRIBUTES.IS_PREGNANT, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "other_clinical_trials",
    type: QuestionType.RADIO,
    question: "Are you currently enrollling in or participating in any other clinical trials?",
    required: true,
    allowBack: true,
    options: [
      { value: "no", label: "No, I am not enrolling in or participating in any other clinical trials" },
      { value: "yes", label: "Yes, I am currently enrolling in or participating in another clinical trial", disqualify: true },
    ],
    disqualifyMessage: "Participants can only participate in one clinical trial at a time.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
    }),
  },

  {
    id: "inPersonAppointments",
    type: QuestionType.RADIO,
    question: "Can you attend up to 6 in-person study visits over approximately 12 months?",
    helpText: "Your local trial site will be located within 30 miles of your home, with the exact address provided upon applying to the trial.",
    required: true,
    allowBack: true,
    options: [
      { value: "yes", label: "Yes, I can attend in-person visits" },
      {
        value: "no",
        label: "No, I cannot attend in-person visits",
        disqualify: true,
      },
      {
        value: "discuss",
        label: "I would like to discuss the requirements with the research team",
      },
    ],
    disqualifyMessage: "This study requires regular in-person visits (2-3 visits per month) over a 12-week period. Upon applying to the trial, you will be provided with a list of study sites and the dates of your visits.",
    attributeMapping: mappers.value({
      yes: [{ attributeDefinitionId: 23479, booleanAttributeValue: true }], // Willing_make_trial_appointments
      no: [{ attributeDefinitionId: 23479, booleanAttributeValue: false }],
      discuss: [], // No specific attribute for wanting to discuss
    }),
  },
]; 